import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Search, Brain, MapPin, GitCompare, FolderHeart, BarChart3,
  Building2, Sparkles, Lock, X, ChevronRight, Star, HelpCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Upgrade Nudge Components for LinkTheMissing
 * 
 * Core Rule: Nudges should never block compassion.
 * They reveal that more insight exists, explain why it matters,
 * and invite upgrade only when intent is high.
 */

// Check if user has pro access
export const useIsPro = () => {
  const { user } = useAuth();
  // Include all tiers that should have Pro access
  return user?.subscription_tier === 'pro' ||
         user?.subscription_tier === 'trial' ||
         user?.subscription_tier === 'professional' || 
         user?.subscription_tier === 'organization' ||
         user?.subscription_plan === 'pro' ||
         user?.subscription_plan === 'trial' ||
         user?.subscription_plan === 'professional' ||
         user?.subscription_plan === 'organization' ||
         user?.is_admin;
};

export const useIsOrg = () => {
  const { user } = useAuth();
  return user?.subscription_tier === 'organization' || user?.is_admin;
};

// ==================== 1. SEARCH RESULTS NUDGE ====================
export function SearchResultsNudge({ relatedCount = 0 }) {
  const isPro = useIsPro();
  const navigate = useNavigate();
  
  if (isPro || relatedCount === 0) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6"
    >
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
              <Search className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                Possible related cases detected
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Some connections require deeper analysis to view.
              </p>
              <Button
                size="sm"
                className="mt-3 bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/pricing')}
                data-testid="nudge-search-upgrade"
              >
                Unlock Potential Matches
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                Upgrade to see algorithmically suggested connections between cases.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ==================== 2. CASE DETAIL NUDGE ====================
export function CaseDetailNudge({ hasStrongMetadata = true }) {
  const isPro = useIsPro();
  const navigate = useNavigate();
  
  if (isPro || !hasStrongMetadata) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
    >
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
              <Brain className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                This case may be connected to others
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Advanced tools can analyze age progression, geographic overlap, and similar cases.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300"
                  onClick={() => navigate('/pricing')}
                  data-testid="nudge-case-detail-upgrade"
                >
                  <Lock className="w-3 h-3 mr-2" />
                  View Possible Matches (Pro)
                </Button>
                <Link 
                  to="/why" 
                  className="text-xs text-slate-500 hover:text-trust-blue-600 dark:hover:text-trust-blue-400 flex items-center gap-1"
                >
                  <HelpCircle className="w-3 h-3" />
                  Why is this feature restricted?
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ==================== 3. MAP VIEW NUDGE (Persistence-Focused) ====================
/**
 * This nudge appears based on ENGAGEMENT behavior, not random presence.
 * It offers persistence (save work) rather than implying features are locked.
 * 
 * Trigger conditions (passed as props):
 * - waypointsAdded >= 2
 * - timeOnMap > 60 seconds  
 * - heatmapToggles >= 2
 * - clusterZooms >= 2
 */
export function MapViewNudge({ 
  waypointsAdded = 0,
  timeOnMap = 0,
  heatmapToggles = 0,
  clusterZooms = 0,
  onDismiss
}) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  
  // Determine if user is engaged enough to show the nudge
  const isEngaged = 
    waypointsAdded >= 2 || 
    timeOnMap >= 30 ||  // Reduced from 60 for better UX
    heatmapToggles >= 2 || 
    clusterZooms >= 2;
  
  // Already logged in or dismissed - don't show
  if (isAuthenticated || dismissed) return null;
  
  if (!isEngaged) return null;
  
  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-24 left-4 right-4 md:left-auto md:right-8 z-[9999] md:max-w-sm"
      >
        <Card className="bg-white/98 dark:bg-slate-900/98 backdrop-blur-sm shadow-xl border-trust-blue-200 dark:border-trust-blue-800">
          <CardContent className="p-4">
            <button 
              onClick={handleDismiss}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="w-10 h-10 rounded-full bg-trust-blue-100 dark:bg-trust-blue-900/50 flex items-center justify-center flex-shrink-0">
                <FolderHeart className="w-5 h-5 text-trust-blue-600 dark:text-trust-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  Continue your investigation
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
                  Save routes, track patterns over time, and get alerts when new cases appear along this corridor.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-trust-blue-600 hover:bg-trust-blue-700"
                    onClick={() => navigate('/signup')}
                    data-testid="nudge-map-signup"
                  >
                    Create free workspace
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-slate-500 hover:text-slate-700"
                    onClick={handleDismiss}
                    data-testid="nudge-map-dismiss"
                  >
                    Keep exploring
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

// ==================== 4. CASE COMPARISON NUDGE ====================
export function CaseComparisonNudge({ onClose }) {
  const navigate = useNavigate();
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mx-auto mb-4">
                <GitCompare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                Compare Cases Side-by-Side
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Case comparison helps identify similarities in age, location, circumstances, and timelines.
              </p>
              <Link 
                to="/why" 
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-trust-blue-600 dark:hover:text-trust-blue-400 mb-4"
              >
                <HelpCircle className="w-3 h-3" />
                Learn how LinkTheMissing approaches case analysis →
              </Link>
              <div className="space-y-3">
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/pricing')}
                  data-testid="nudge-compare-upgrade"
                >
                  Upgrade to Professional to Compare Cases
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={onClose}
                >
                  Continue viewing cases
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// ==================== 5. WORKSPACE NUDGE ====================
export function WorkspaceNudge({ savedCasesCount = 0 }) {
  const isPro = useIsPro();
  const navigate = useNavigate();
  
  if (isPro || savedCasesCount < 2) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4"
    >
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <FolderHeart className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                Tracking multiple cases?
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                A Professional workspace lets you organize, annotate, and monitor case updates in one place.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 flex-shrink-0"
              onClick={() => navigate('/pricing')}
              data-testid="nudge-workspace-upgrade"
            >
              Upgrade for Case Tracking
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ==================== 6. ANALYTICS NUDGE ====================
export function AnalyticsNudge() {
  const isPro = useIsPro();
  const navigate = useNavigate();
  
  if (isPro) return null;
  
  return (
    <div className="relative">
      {/* Blurred preview */}
      <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
        <Card className="max-w-sm mx-4">
          <CardContent className="p-6 text-center">
            <BarChart3 className="w-10 h-10 text-indigo-600 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
              Insights available
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Advanced analytics reveal geographic patterns and timeline trends across cases.
            </p>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => navigate('/pricing')}
              data-testid="nudge-analytics-upgrade"
            >
              Unlock Advanced Analytics
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== 7. ORGANIZATION NUDGE ====================
export function OrganizationNudge({ trigger = 'bulk' }) {
  const isOrg = useIsOrg();
  const navigate = useNavigate();
  
  if (isOrg) return null;
  
  const messages = {
    bulk: 'Working with multiple cases at once?',
    export: 'Need to export data for reporting?',
    api: 'Looking to integrate with your systems?',
  };
  
  return (
    <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
              {messages[trigger] || 'Working as a team or organization?'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Verified organizations unlock bulk tools, API access, and advanced pattern analysis.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 flex-shrink-0"
            onClick={() => navigate('/contact?subject=Organization%20Plan%20Inquiry')}
            data-testid="nudge-org-upgrade"
          >
            Apply for Organization Access
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== 8. FOOTER NUDGE ====================
export function FooterNudge() {
  const isPro = useIsPro();
  
  if (isPro) return null;
  
  return (
    <div className="border-t border-slate-200 dark:border-slate-800 py-4 mt-8">
      <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
        <Sparkles className="w-4 h-4" />
        <span>Want deeper insight into case connections?</span>
        <Link 
          to="/pricing" 
          className="text-blue-600 hover:text-blue-700 font-medium"
          data-testid="nudge-footer"
        >
          View Plans →
        </Link>
      </div>
    </div>
  );
}

// ==================== 9. EMPTY STATE NUDGE ====================
export function EmptyStateNudge({ type = 'saved' }) {
  const isPro = useIsPro();
  const navigate = useNavigate();
  
  const content = {
    saved: {
      icon: Star,
      title: 'Save cases to track updates',
      description: 'Professional users can organize and monitor cases over time.',
      cta: 'Upgrade to Start Tracking',
    },
    workspace: {
      icon: FolderHeart,
      title: 'No cases in your workspace',
      description: 'Start tracking cases and get organized with a Professional workspace.',
      cta: 'Upgrade for Workspace',
    },
    matches: {
      icon: GitCompare,
      title: 'No matches yet',
      description: 'Professional tools help surface connections that aren\'t visible at a glance.',
      cta: 'Unlock Match Detection',
    },
  };
  
  const { icon: Icon, title, description, cta } = content[type] || content.saved;
  
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-sm mx-auto">
        {description}
      </p>
      {!isPro && (
        <Button
          variant="outline"
          onClick={() => navigate('/pricing')}
          data-testid={`nudge-empty-${type}`}
        >
          {cta}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}

// ==================== 10. ONE-TIME GENTLE MODAL ====================
export function GentleUpgradeModal({ onClose }) {
  const navigate = useNavigate();
  
  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
        >
          <Card className="max-w-md w-full">
            <CardContent className="p-6">
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/50 dark:to-orange-900/50 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  You're doing important work
                </h2>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  Many users upgrade to access tools that help identify connections and patterns across cases.
                </p>
                <div className="space-y-3">
                  <Button
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={() => {
                      onClose();
                      navigate('/pricing');
                    }}
                    data-testid="nudge-gentle-modal-upgrade"
                  >
                    Explore Professional Tools
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-slate-500"
                    onClick={onClose}
                  >
                    Continue using free version
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// ==================== ACTION TRACKER (for gentle modal trigger) ====================
const ACTION_THRESHOLD = 5;
const STORAGE_KEY = 'ltm_user_actions';
const MODAL_SHOWN_KEY = 'ltm_gentle_modal_shown';

export function useActionTracker() {
  const [showGentleModal, setShowGentleModal] = useState(false);
  const isPro = useIsPro();
  
  const trackAction = () => {
    if (isPro) return;
    
    // Check if modal was already shown
    const modalShown = localStorage.getItem(MODAL_SHOWN_KEY);
    if (modalShown) return;
    
    // Increment action count
    const actions = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10) + 1;
    localStorage.setItem(STORAGE_KEY, actions.toString());
    
    // Show modal after threshold
    if (actions >= ACTION_THRESHOLD) {
      setShowGentleModal(true);
      localStorage.setItem(MODAL_SHOWN_KEY, 'true');
    }
  };
  
  const closeModal = () => {
    setShowGentleModal(false);
  };
  
  return { trackAction, showGentleModal, closeModal };
}

// ==================== LOCKED FEATURE WRAPPER ====================
export function LockedFeature({ children, feature = 'feature', requiredTier = 'professional' }) {
  const isPro = useIsPro();
  const isOrg = useIsOrg();
  const navigate = useNavigate();
  
  const hasAccess = requiredTier === 'organization' ? isOrg : isPro;
  
  if (hasAccess) {
    return children;
  }
  
  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none blur-[2px]">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 dark:bg-slate-900/60 rounded-lg gap-2">
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => navigate('/pricing')}
          data-testid={`locked-${feature}`}
        >
          <Lock className="w-4 h-4" />
          Unlock {feature}
        </Button>
        <Link 
          to="/why" 
          className="text-xs text-slate-500 hover:text-trust-blue-600 dark:hover:text-trust-blue-400 flex items-center gap-1"
        >
          <HelpCircle className="w-3 h-3" />
          Why is this restricted?
        </Link>
      </div>
    </div>
  );
}
